import { useAuth } from "@clerk/react"
import { createORPCClient } from "@orpc/client"
import { RPCLink } from "@orpc/client/fetch"
import type { ContractRouterClient } from "@orpc/contract"
import { ExchangeRateSource, type exchangeRateContract } from "@repo/api-dtos"
import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { getApiUrl } from "@/integrations/orpc/client"

type ExchangeRateClient = ContractRouterClient<{
	exchangeRate: typeof exchangeRateContract
}>

/** EUR-based conversion rates (1 EUR = rates[code]), cached for 5 minutes. */
export function useExchangeRates() {
	const { getToken, isSignedIn } = useAuth()

	const client = useMemo<ExchangeRateClient>(
		() =>
			createORPCClient(
				new RPCLink({
					url: `${getApiUrl()}/orpc`,
					headers: async () => {
						try {
							const token = await getToken()
							return token
								? {
										"Content-Type": "application/json",
										Authorization: `Bearer ${token}`,
									}
								: { "Content-Type": "application/json" }
						} catch (error) {
							console.error(
								"[oRPC ExchangeRate] Error getting auth token:",
								error,
							)
							return { "Content-Type": "application/json" }
						}
					},
					fetch: async (input, init) =>
						fetch(input, { ...init, credentials: "include" }),
				}),
			),
		[getToken],
	)

	const { data: rates = null, isLoading } = useQuery({
		queryKey: ["exchange-rates", "EUR"],
		queryFn: async () => {
			const response = await client.exchangeRate.getRates({
				sourceCurrency: ExchangeRateSource.EUR,
			})
			return response.conversionRates
		},
		enabled: !!isSignedIn,
		staleTime: 5 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
	})

	return { rates, isLoading }
}
